/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	Children,
	isValidElement,
	useContext,
	type ComponentProps,
	type PropsWithChildren,
	type ReactElement,
	type ReactNode,
} from 'react';
import { cssMap } from '@compiled/react';

import { jsx } from '@atlaskit/css';
import {
	DEFAULT_IMAGE_HEIGHT,
	DEFAULT_IMAGE_WIDTH,
	MEDIA_SINGLE_GUTTER_SIZE,
	wrappedLayouts,
} from '@atlaskit/editor-common/media-single/constants';
import { calcMediaSinglePixelWidth } from '@atlaskit/editor-common/media-single/utils';
import { WidthContext } from '@atlaskit/editor-common/ui';
import {
	akEditorDefaultLayoutWidth,
	akEditorFullWidthLayoutWidth,
	akEditorGutterPadding,
	akEditorWideLayoutWidth,
} from '@atlaskit/editor-shared-styles/constants';
import type {
	Media as OriginalMedia,
	MediaSingle as OriginalMediaSingle,
} from '@atlaskit/renderer/nodes';
import { token } from '@atlaskit/tokens';

import type { LightNodeProps } from '../LightNodeObserver.ts';

type MediaProps = ComponentProps<typeof OriginalMedia> & { nodeType?: string };

const styles = cssMap({
	figure: {
		maxWidth: '100cqw',
	},
	centered: {
		marginTop: token('space.300'),
		marginRight: 'auto',
		marginBottom: token('space.300'),
		marginLeft: 'auto',
	},
	nested: {
		maxWidth: '100%',
	},
	breakout: {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- Percentage offset is required for canonical breakout centering, not visual spacing.
		marginLeft: '50%',
		transform: 'translateX(-50%)',
	},
	alignStart: {
		marginBlockStart: token('space.300'),
		marginBlockEnd: token('space.300'),
		marginInlineStart: token('space.0'),
		marginInlineEnd: 'auto',
	},
	alignEnd: {
		marginBlockStart: token('space.300'),
		marginBlockEnd: token('space.300'),
		marginInlineStart: 'auto',
		marginInlineEnd: token('space.0'),
	},
	wrapLeft: {
		float: 'left',
		marginTop: token('space.150'),
		marginRight: token('space.150'),
		marginBottom: token('space.150'),
		marginLeft: 'auto',
	},
	wrapRight: {
		float: 'right',
		marginTop: token('space.150'),
		marginRight: 'auto',
		marginBottom: token('space.150'),
		marginLeft: token('space.150'),
	},
	media: {
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
		maxHeight: '100%',
		maxWidth: '100%',
	},
});

const isMediaElement = (node: ReactNode): node is ReactElement<MediaProps> => {
	if (!isValidElement<MediaProps>(node)) {
		return false;
	}

	return node.props.nodeType === 'media' || ['external', 'file', 'link'].includes(node.props.type);
};

const findMediaElement = (node: ReactNode): ReactElement<MediaProps> | null => {
	let current = Children.toArray(node)[0];

	while (current !== undefined) {
		if (isMediaElement(current)) {
			return current;
		}

		if (!isValidElement<PropsWithChildren<unknown>>(current)) {
			return null;
		}

		current = Children.toArray(current.props.children)[0];
	}

	return null;
};

const getMediaDimension = (dimension: number | null | undefined, fallback: number): number =>
	dimension && Number.isFinite(dimension) && dimension > 0 ? dimension : fallback;

const getMediaFallbackWidth = (
	layout: ComponentProps<typeof OriginalMediaSingle>['layout'],
): number => {
	switch (layout) {
		case 'full-width':
			return akEditorFullWidthLayoutWidth;
		case 'wide':
			return akEditorWideLayoutWidth;
		default:
			return akEditorDefaultLayoutWidth;
	}
};

const getMediaContentWidth = (
	props: ComponentProps<typeof OriginalMediaSingle>,
	containerWidth: number,
): number => {
	if (props.isInsideOfBlockNode || props.rendererAppearance === 'comment') {
		return containerWidth;
	}

	const horizontalPadding =
		props.rendererAppearance === 'full-page' ? akEditorGutterPadding * 2 : 0;
	const maximumWidth =
		props.rendererAppearance === 'full-width'
			? akEditorFullWidthLayoutWidth
			: akEditorDefaultLayoutWidth;

	return Math.min(maximumWidth, containerWidth - horizontalPadding);
};

const getMediaFigureWidth = (
	props: ComponentProps<typeof OriginalMediaSingle>,
	intrinsicWidth: number,
	measuredWidth: number,
): number => {
	const containerWidth = measuredWidth || getMediaFallbackWidth(props.layout);
	const contentWidth = getMediaContentWidth(props, containerWidth);
	const width = calcMediaSinglePixelWidth({
		containerWidth,
		contentWidth,
		gutterOffset: MEDIA_SINGLE_GUTTER_SIZE,
		layout: props.layout,
		origWidth: intrinsicWidth,
		width: props.width,
		widthType: props.widthType,
	});

	return Number.isFinite(width) && width > 0
		? Math.min(width, containerWidth)
		: Math.min(intrinsicWidth, containerWidth);
};

export const MediaSingle = (
	props: ComponentProps<typeof OriginalMediaSingle> & LightNodeProps,
): ReactElement => {
	const { width: measuredWidth } = useContext(WidthContext);
	const [media, caption] = Children.toArray(props.children);
	const mediaElement = findMediaElement(media);
	const mediaWidth = getMediaDimension(mediaElement?.props.width, DEFAULT_IMAGE_WIDTH);
	const mediaHeight = getMediaDimension(mediaElement?.props.height, DEFAULT_IMAGE_HEIGHT);
	const figureWidth = getMediaFigureWidth(props, mediaWidth, measuredWidth);
	const isNested =
		props.isInsideOfBlockNode ||
		props.isInsideOfInlineExtension ||
		props.rendererAppearance === 'comment';
	const isBreakout = props.layout === 'wide' || props.layout === 'full-width';
	const isCentered = !wrappedLayouts.includes(props.layout);
	const alt = mediaElement?.props.alt;

	return (
		<figure
			ref={props.lightNodeRef}
			css={[
				styles.figure,
				isCentered && styles.centered,
				isNested && styles.nested,
				isBreakout && !isNested && styles.breakout,
				props.layout === 'align-start' && styles.alignStart,
				props.layout === 'align-end' && styles.alignEnd,
				props.layout === 'wrap-left' && styles.wrapLeft,
				props.layout === 'wrap-right' && styles.wrapRight,
			]}
			data-layout={props.layout}
			data-node-type="mediaSingle"
			data-renderer-start-pos={props.dataAttributes?.['data-renderer-start-pos']}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Figure width is calculated from runtime container, authored width and media dimensions.
			style={{ width: figureWidth }}
		>
			<div
				aria-hidden={alt ? undefined : true}
				aria-label={alt || undefined}
				css={styles.media}
				role={alt ? 'img' : undefined}
				style={{
					aspectRatio: `${mediaWidth} / ${mediaHeight}`,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Full figure width reserves the authored media geometry before activation.
					width: '100%',
				}}
			/>
			{props.allowCaptions && caption}
		</figure>
	);
};
